/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/si";
import { useTranslation } from "react-i18next";

// @mui material components
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavBar from "app/layout/TopNavBar";
import Footer from "examples/Footer";
import EventCalendar from "examples/Calendar";
import CalendarServices from "app/services/CalendarServices";
import EventDetailsDialog from "components/LH/Calendar/EventDetailsDialog";

const getCurrentMonth = () => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
};

const INITIAL_MONTH = getCurrentMonth();
const getCalendarEventDetails = (event) => ({
  title: event.title,
  text: event.extendedProps?.text,
  date: event.extendedProps?.date,
  startTime: event.extendedProps?.startTime,
  endTime: event.extendedProps?.endTime,
});

const getAdjacentMonthName = (month, offset, monthNames) => {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(year, monthNumber - 1 + offset, 1);
  return monthNames[date.getMonth()];
};

const getCalendarTitle = (dateInfo, monthNames) => {
  const calendarDate = dateInfo.start || dateInfo.date || dateInfo;
  const date = calendarDate instanceof Date
    ? calendarDate
    : new Date(calendarDate.year, calendarDate.month, calendarDate.day || 1);

  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

const formatEventTime = (time) => time?.replace("-", ":") || "";
const formatEventDate = (date, language) => moment(date, "YYYY-MM-DD")
  .locale(language === "si" ? "si" : "en")
  .format("dddd, MMMM D, YYYY");

const getAgendaDateTitle = (dateInfo, monthNames, dayNames) => {
  const calendarDate = dateInfo.date || dateInfo;
  const isNativeDate = calendarDate instanceof Date;
  const year = isNativeDate ? calendarDate.getFullYear() : calendarDate.year;
  const monthNumber = isNativeDate ? calendarDate.getMonth() + 1 : calendarDate.month + 1;
  const day = isNativeDate ? calendarDate.getDate() : calendarDate.day;
  const dayNumber = new Date(year, monthNumber - 1, day).getDay();

  return `${dayNames[dayNumber]}, ${monthNames[monthNumber - 1]} ${day}, ${year}`;
};

const toCalendarEvents = (events) => events.map((event) => ({
  id: event.id,
  title: `${formatEventTime(event.startTime)} - ${formatEventTime(event.endTime)}\n${event.text}`,
  start: event.start,
  end: event.end,
  className: "success",
  extendedProps: {
    eventType: event.eventType,
    date: event.date,
    startTime: event.startTime,
    endTime: event.endTime,
    text: event.text,
  },
}));

function Calendar() {
  const { t, i18n } = useTranslation();
  const { getEvents } = CalendarServices();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [month, setMonth] = useState(INITIAL_MONTH);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const calendarMonths = t("calendar.months", { returnObjects: true });
  const calendarDays = t("calendar.days", { returnObjects: true });
  const calendarShortDays = t("calendar.shortDays", { returnObjects: true });
  const previousMonthName = getAdjacentMonthName(month, -1, calendarMonths);
  const nextMonthName = getAdjacentMonthName(month, 1, calendarMonths);

  useEffect(() => {
    let active = true;

    const loadEvents = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getEvents(month);
        if (active) {
          setEvents(toCalendarEvents(data));
        }
      } catch (requestError) {
        if (active) {
          setEvents([]);
          setError(requestError.response?.data?.message || t("calendar.loadError"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      active = false;
    };
  }, [getEvents, month]);

  const handleDatesSet = ({ view }) => {
    const currentStart = view.currentStart;
    const nextMonth = `${currentStart.getFullYear()}-${String(currentStart.getMonth() + 1).padStart(2, "0")}`;
    setMonth((currentMonth) => (currentMonth === nextMonth ? currentMonth : nextMonth));
  };

  const handleEventClick = ({ event }) => {
    setSelectedEvent(getCalendarEventDetails(event));
  };

  return (
    <DashboardLayout>
      <TopNavBar />
      <MDBox pt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ minHeight: 640 }}>
            <MDBox
              mb={2}
              px={2}
              py={1.5}
              sx={{ backgroundColor: "#c7d9e9", borderBottom: "1px solid #b5cadc", borderRadius: 1 }}
            >
              <MDTypography variant="h5" fontWeight="medium">
                {t("calendar.menuTitle")}
              </MDTypography>
            </MDBox>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {!loading && !error && events.length === 0 ? (
              <Alert
                severity="error"
                variant="outlined"
                sx={{
                  width: "100%",
                  mb: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                }}
              >
                {t("calendar.empty")}
              </Alert>
            ) : null}
            <MDBox position="relative" minHeight={640}>
              <EventCalendar
                // initialView={isMobile ? "listMonth" : "dayGridMonth"}
                initialDate={`${INITIAL_MONTH}-01`}
                validRange={{ start: `${INITIAL_MONTH}-01` }}
                firstDay={1}
                events={events}
                displayEventTime={false}
                datesSet={handleDatesSet}
                eventClick={handleEventClick}
                eventContent={({ event, view }) => (
                  <span className="calendar-event-content">
                    <span className="calendar-event-label">
                      <span className="calendar-event-time">{formatEventTime(event.extendedProps.startTime)} - {formatEventTime(event.extendedProps.endTime)}</span>
                      { isMobile && view.type === "dayGridMonth"? null :
                        <span className="calendar-event-name">{event.extendedProps.text}</span>
                     }
                    </span>
                    {!isMobile ? <Icon className="calendar-event-info" fontSize="inherit">info_outline</Icon> : null}
                  </span>
                )}
                eventDidMount={({ event, el }) => {
                  el.setAttribute("title", `${event.extendedProps.date} ${event.extendedProps.startTime} - ${event.extendedProps.endTime}`);
                }}
                enhancedToolbar
                toolbarAttribution
                toolbarContext={{
                  flagSrc: "https://flagcdn.com/24x18/lk.png",
                  locationText: "Colombo, Western Province, Sri Lanka",
                }}
                headerToolbar={{
                  left: "",
                  center: "prev title next",
                  right: "dayGridMonth,listMonth",
                }}
                buttonText={{
                  today: t("calendar.today"),
                  prev: previousMonthName,
                  next: nextMonthName,
                  month: t("calendar.month"),
                  listMonth: t("calendar.agenda"),
                }}
                buttonIcons={{
                  prev: "chevron-left",
                  next: "chevron-right",
                }}
                views={{
                  dayGridMonth: { titleFormat: (dateInfo) => getCalendarTitle(dateInfo, calendarMonths) },
                  listMonth: { titleFormat: (dateInfo) => getCalendarTitle(dateInfo, calendarMonths) },
                }}
                dayCellClassNames={({ date }) => (date.getDay() === 2 ? "calendar-tuesday" : "")}
                dayHeaderContent={({ date }) => calendarShortDays[date.getDay()]}
                listDayFormat={(dateInfo) => getAgendaDateTitle(dateInfo, calendarMonths, calendarDays)}
                listDaySideFormat={false}
                toolbarHint={isMobile ? t("calendar.tapForDetails") : ""}
                locales={[{
                  code: "si",
                  week: { dow: 1 },
                  buttonText: {
                    today: t("calendar.today"),
                    month: t("calendar.month"),
                    listMonth: t("calendar.agenda"),
                  },
                  monthNames: calendarMonths,
                  monthNamesShort: calendarMonths,
                  dayNames: calendarDays,
                  dayNamesShort: calendarDays,
                }]}
                locale="si"
                height="auto"
              />
              {loading ? (
                <MDBox
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 2 }}
                >
                  <CircularProgress color="info" />
                </MDBox>
              ) : null}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <EventDetailsDialog
        event={selectedEvent}
        events={events}
        attributionUrl="https://app.lakhandahana.com/"
        contextFlagSrc="https://flagcdn.com/24x18/lk.png"
        contextText="Sri Lanka | Colombo, Western Province, Sri Lanka"
        dateLabel={t("calendar.date")}
        formatDate={(date) => formatEventDate(date, i18n.language)}
        fields={[
          { label: t("calendar.from"), value: (item) => formatEventTime(item.startTime) },
          { label: t("calendar.to"), value: (item) => formatEventTime(item.endTime) },
        ]}
        getEventDetails={getCalendarEventDetails}
        getEventSx={() => (i18n.language.startsWith("si") ? {
          backgroundColor: "#e8f5e9",
          color: "#1b5e20",
          border: "1px solid #a5d6a7",
        } : {})}
        getEventSummary={(item) => item.text}
        compactInlineFields
        inlineFields
        onClose={() => setSelectedEvent(null)}
        onNavigate={setSelectedEvent}
        title={t("calendar.event")}
      />
    </DashboardLayout>
  );
}

export default Calendar;
